import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { CardTitle } from '@zoopla-eng/zds-components/headings';
import { MaskedInputField } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Select, SelectOption } from '@zoopla-eng/zds-components/select';
import { formatter as currencyFormatter } from '@zoopla/shared/currency';
import { ChangeEvent, FormEventHandler, ReactNode, useState } from 'react';
import {
    BUYER_TYPE_OPTIONS,
    calculateStampDuty,
    isBuyerType
} from './helpers/calculate-stamp-duty';
import { customGridStyle } from './stamp-duty-calculator.styles.css';
import {
    trackCalculate,
    trackPriceClick,
    trackPropertyStatus
} from './stamp-duty-calculator.tracking';

export type StampDutyCalculatorProps = {
    show: boolean;
    inputValue: string;
    trackingCategory: string;
    title?: string;
    body?: ReactNode;
};

export const StampDutyCalculator = ({
    show,
    inputValue: initialInputValue,
    trackingCategory,
    title,
    body
}: StampDutyCalculatorProps) => {
    const [selectedBuyerOption, setSelectedBuyerOption] = useState({
        id: '',
        text: 'Please select'
    });

    const [inputValue, setInputValue] = useState<string>(initialInputValue);

    const [result, setResult] = useState<number | undefined>(undefined);

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const onSubmitHandler: FormEventHandler = (e) => {
        e.preventDefault();

        trackCalculate(trackingCategory);

        if (!isBuyerType(selectedBuyerOption.id)) return;

        const price = calculateStampDuty(
            selectedBuyerOption.id,
            parseInt(inputValue)
        );
        setResult(price);
    };

    const onSelectedOptionChangeHandler = (option: SelectOption) => {
        setSelectedBuyerOption(option);
        setResult(undefined);

        if (isBuyerType(option.id)) {
            trackPropertyStatus(option.id, trackingCategory);
        }
    };

    if (!show) return null;

    return (
        <Box pt="M100" fontGroup="ladle" data-testid="stamp-duty-calculator">
            <Box pb="M100">
                <Divider direction="horizontal" />
            </Box>
            <Box pb="S500">
                <CardTitle>{title || 'Stamp duty calculator'}</CardTitle>
            </Box>
            {body || (
                <Box pt="S100" pb="M100">
                    We’ve updated our calculator to take into account the stamp
                    duty cut announced by the chancellor on 23 September 2022.{' '}
                    <Link
                        href={
                            '/discover/property-news/what-the-stamp-duty-cut-means-for-buyers/'
                        }
                    >
                        Read more stamp duty news
                    </Link>{' '}
                    and{' '}
                    <Link href={'/discover/buying/stamp-duty-calculator/'}>
                        how stamp duty works
                    </Link>
                    .
                </Box>
            )}

            <form onSubmit={onSubmitHandler}>
                <div className={customGridStyle}>
                    <MaskedInputField
                        type="number"
                        name="stamp-duty-price"
                        label="Price"
                        placeholder="Price"
                        value={inputValue}
                        onChange={onInputChange}
                        onFocus={() => trackPriceClick(trackingCategory)}
                        mask={(value) =>
                            value?.toString()?.length
                                ? currencyFormatter.format(Number(value))
                                : ''
                        }
                        data-testid="stamp-duty-price"
                        min="0.0"
                        step="1000"
                    />
                    <Select
                        selectedOption={selectedBuyerOption}
                        onSelectedOptionChange={(option) => {
                            onSelectedOptionChangeHandler(option);
                        }}
                        options={BUYER_TYPE_OPTIONS.map((option) => ({
                            id: option.buyerType,
                            text: option.displayedText
                        }))}
                        label="I am..."
                        data-testid="stamp-duty-buyer-option"
                    />
                    <Box pt="S400" bpM100={{ pt: 0 }}>
                        <Button
                            fw
                            category="secondary"
                            data-testid="stamp-duty-submit"
                            disabled={!selectedBuyerOption.id}
                            type="submit"
                        >
                            Calculate
                        </Button>
                    </Box>
                </div>
            </form>
            {typeof result !== 'undefined' && (
                <>
                    <Box centered py="M300" fw>
                        <Grid rowGap="S500">
                            <Grid.Item colspan={12}>
                                <p>From 23rd September 2022</p>
                                <Box fontGroup="mug">
                                    <b>
                                        {currencyFormatter.format(result || 0)}
                                    </b>
                                </Box>
                            </Grid.Item>
                            {selectedBuyerOption.id !== 'first-time-buyer' ? (
                                <Grid.Item colspan={12}>
                                    <p>
                                        Need to sell your home to buy this
                                        property?
                                    </p>
                                    <Link href="/home-values/">
                                        See how much your home is worth
                                    </Link>
                                </Grid.Item>
                            ) : null}
                        </Grid>
                    </Box>

                    <Box fontGroup="spoon">
                        <p>
                            This stamp duty calculator provides illustrative
                            figures for stamp duty in England and Northern
                            Ireland. We try to make sure that the information is
                            accurate at the time of publishing but the property
                            market moves fast and some information may now be
                            out of date. This calculator is not intended to
                            provide financial advice and should not be relied on
                            for any purposes. We recommend you seek advice from
                            a financial advisor in relation to any financial
                            decision.
                        </p>
                    </Box>
                </>
            )}
        </Box>
    );
};
